/**
 * Warning-ZONE centroid identity glyph (#475 + #473, W1 Slice 2 zone-legibility).
 *
 * The four coloured warning-zone families — CAP extreme-weather, power outages,
 * road closures, estimated comms degradation — each render a big translucent FILL
 * (or line/disc) the operator must be able to NAME at a glance. This module is the
 * single place that:
 *
 *   1. maps a zone TYPE → its CAHS pictograph key ({@link ZONE_GLYPH}), the
 *      founder-signed vocabulary locked by the #472 parity test (G10); and
 *   2. builds the centred identity-glyph billboard ({@link zoneGlyphBillboard}) —
 *      the shipped act-chip PLATE treatment (solid light plate + dark BULKED symbol,
 *      iconRegistry `renderCahsZoneChip`) with a thin zone-coloured RIM, so SHAPE is
 *      primary and COLOUR secondary (the founder-signed rule, 4-seat gate G8/G9).
 *
 * Z-ORDER (#473 / G6): the glyph carries `disableDepthTestDistance: Infinity` AND a
 * toward-viewer `eyeOffset` ({@link ZONE_GLYPH_EYE_OFFSET}), so the identity glyph
 * always floats CLEAR of every ground-clamped / height-0 zone fill — including a
 * DIFFERENT zone layer's overlapping fill — instead of being occluded by the terrain
 * classification pass. It tiers in at regional+ (the §3B.8 / PART 8 country-zoom
 * glyph-drop, matching every other CAHS map glyph), STATIC (Law 6 — a CAHS glyph
 * never animates).
 */
import * as Cesium from 'cesium';
import { renderCahsZoneChip } from '../iconRegistry';
import type { CahsGlyphKey } from '../assets/cahs/index.generated';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

/**
 * The founder-signed (2026-06-30) zone-type → CAHS pictograph map for the STATIC zone families
 * that still render the zone chip. CAP extreme-weather resolves its glyph per-event through
 * `styles/capHazards.capEventGlyph` (tornado / hurricane / ice-storm / blizzard / severe-storm /
 * extreme-heat), so it is not a single key here. Locked literal — the #472 parity test pins this
 * so the vocabulary can never silently drift (G10).
 *
 * POWER OUTAGE was removed here by founder NIGHT ruling 4 (2026-07-06): the outage family is
 * unified onto the EXACT-ACT disc (renderDiscGlyph('power', …) in PowerOutagesLayer), so it no
 * longer rides the zone chip. Road closures + comms degradation remain ambient zone-chip families.
 */
export const ZONE_GLYPH = {
  road_closure: 'road-closure',
  comms_degradation: 'comms-tower',
} as const satisfies Record<string, CahsGlyphKey>;

export type ZoneGlyphType = keyof typeof ZONE_GLYPH;

/** Default centred identity-glyph pixel size (matches the CAP standalone glyph's
 *  #353 24px legible hit target). */
export const ZONE_GLYPH_SIZE = 24;

/**
 * Eye-space offset that lifts a zone identity glyph CLEAR of the zone fills (#473 /
 * G6). z "points into the screen" (Cesium eye coords are left-handed), so a NEGATIVE
 * z pulls the glyph toward the viewer — biasing its billboard collection in front of
 * any other zone layer's translucent fill so a click-and-read never hides under an
 * overlapping coloured area. Held small (−1 km, mirroring the act-chip CHIP_EYE_OFFSET)
 * so it never crosses the near plane at any zoom the glyph shows (regional+).
 */
export const ZONE_GLYPH_EYE_OFFSET = new Cesium.Cartesian3(0, 0, -1000);

/**
 * Build the centred warning-zone identity-glyph billboard (#475/#473). `glyph` is
 * the CAHS key (a {@link ZONE_GLYPH} value or a `capEventGlyph` result); `rimColor`
 * is the zone's hue (carried on the chip rim — the lifecycle-resolved colour for CAP:
 * event hue fresh, ACK-STEEL acked). Returns `undefined` when the glyph (or its
 * category frame) is unknown so callers fall back to no glyph. CENTER-anchored,
 * STATIC (Law 6), regional+ gated, and floated clear of the fills (eyeOffset +
 * disableDepthTest).
 */
export function zoneGlyphBillboard(
  glyph: CahsGlyphKey,
  opts: { rimColor: string; rimWidth?: number; size?: number },
): Cesium.BillboardGraphics.ConstructorOptions | undefined {
  const image = renderCahsZoneChip(glyph, { color: opts.rimColor, width: opts.rimWidth });
  if (!image) return undefined;
  const size = opts.size ?? ZONE_GLYPH_SIZE;
  return {
    image,
    width: size,
    height: size,
    verticalOrigin: Cesium.VerticalOrigin.CENTER,
    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
    // #473 / G6 — float the identity glyph clear of every zone fill.
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
    eyeOffset: ZONE_GLYPH_EYE_OFFSET,
    // PART 8 country-zoom glyph-drop: a busy glyph muds at country scale, so it tiers
    // in at regional+ exactly like every other CAHS map glyph.
    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M),
  };
}
